import { FC } from 'react';
import {
  useCampaignContext,
  useEditorContext,
  useUnsavedPromptContext,
} from 'modules/campaigns/editor/context';
import { CampaignEditorView } from 'modules/campaigns/editor/types';
import { useDisplayPriorityContext } from 'modules/placements/context/DisplayPriorityContext';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { Edit16, LongArrowRight } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { DateFormat, TimeFormat, localizeDateTime } from 'utils/l10n';
import { CampaignFormField, ReadOnlyField } from '../../FormFields';
import { getCurrentOrEditedOrder, getHasDisplayPriority } from '../../utils';
import {
  CriteriaListWrapper,
  FieldWrapper,
  LightText,
  SectionHeader,
  SectionWrapper,
  WithBulletPoint,
} from '../Sections.styled';

export const DISPLAY_PRIORITY_SELECTION_BUTTON_TEST_ID = 'display-priority-selection-button';
export const DISPLAY_SECTION_TEST_ID = 'embedded-delivery-section';

export const DisplaySection = () => {
  const { transitionTo } = useUnsavedPromptContext();
  const {
    canDraftMessaging,
    setIsDisplayPriorityTakeoverOpen,
    setIsDisplayUntilTakeoverOpen,
    campaignEditorView,
  } = useEditorContext();
  const { campaign } = useCampaignContext();
  const { campaignDisplayPriorityOrder, isDirty } = useDisplayPriorityContext();
  const { embeddedPriorityOrder, isJourneyAssociated, displayUntil } = campaign;
  const currentOrEditedOrder = getCurrentOrEditedOrder(campaignDisplayPriorityOrder, campaign.id);
  const hasDisplaySet = getHasDisplayPriority(embeddedPriorityOrder) || isDirty;

  const endDateFormatted =
    displayUntil?.endDate &&
    localizeDateTime(new Date(displayUntil.endDate), {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateStyle: DateFormat.Numeric,
      timeStyle: TimeFormat.Locale,
      showTime: true,
    });
  const searchQuery = displayUntil?.searchQuery?.searchQueries[0];

  const handleOpenDisplayPriority = () => {
    transitionTo(() => setIsDisplayPriorityTakeoverOpen(true));
  };

  const handleOpenDisplayUntil = () => {
    transitionTo(() => setIsDisplayUntilTakeoverOpen(true));
  };

  const PermissionTooltip: FC<React.PropsWithChildren> = ({ children }) => {
    return (
      <Tooltip
        isEnabled={!canDraftMessaging}
        tooltipContent={'You do not have permission to edit campaigns'}
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <SectionWrapper data-test={DISPLAY_SECTION_TEST_ID}>
      <SectionHeader>
        {campaignEditorView === CampaignEditorView.Summary && 'Display priority'}
      </SectionHeader>
      <CampaignFormField labelText="Display priority" $removeMarginBottom={!isJourneyAssociated}>
        <FieldWrapper>
          <ReadOnlyField name="displayPriority">
            {hasDisplaySet ? `${currentOrEditedOrder} campaigns` : <LightText>Not set</LightText>}
          </ReadOnlyField>
          {campaignEditorView === CampaignEditorView.Edit && (
            <PermissionTooltip>
              <Button
                isDisabled={!canDraftMessaging}
                onClick={handleOpenDisplayPriority}
                data-test={DISPLAY_PRIORITY_SELECTION_BUTTON_TEST_ID}
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.SECONDARY}
              >
                {hasDisplaySet && <Edit16 />}
                {hasDisplaySet ? 'Edit' : 'Select display priority'}{' '}
                <LongArrowRight height={16} width={16} />
              </Button>
            </PermissionTooltip>
          )}
        </FieldWrapper>
      </CampaignFormField>
      {isJourneyAssociated && (
        <CampaignFormField
          labelText="Display until"
          restrictionText={
            campaignEditorView === CampaignEditorView.Edit &&
            'This message will display until any of the following occurs.'
          }
          $removeMarginBottom
        >
          <FieldWrapper>
            <ReadOnlyField name="displayUntil">
              <CriteriaListWrapper>
                <WithBulletPoint>Journey is off or archived</WithBulletPoint>
                {endDateFormatted && (
                  <WithBulletPoint>{`Date is ${endDateFormatted}`}</WithBulletPoint>
                )}
                {searchQuery && (
                  <WithBulletPoint>{`User field changes: ${searchQuery.field}`}</WithBulletPoint>
                )}
              </CriteriaListWrapper>
            </ReadOnlyField>
            {campaignEditorView === CampaignEditorView.Edit && (
              <PermissionTooltip>
                <Button
                  isDisabled={!canDraftMessaging}
                  onClick={handleOpenDisplayUntil}
                  data-test={DISPLAY_PRIORITY_SELECTION_BUTTON_TEST_ID}
                  size={BUTTON_SIZES.SMALL}
                  variant={BUTTON_VARIANTS.SECONDARY}
                >
                  <Edit16 />
                  Edit
                  <LongArrowRight height={16} width={16} />
                </Button>
              </PermissionTooltip>
            )}
          </FieldWrapper>
        </CampaignFormField>
      )}
    </SectionWrapper>
  );
};
